import type { Card, FluencyCategoryId } from "../../content/types";
import { GRADUATION_DAYS, heatFor } from "../../engine/srs";
import { isMastered } from "../../engine/mastery";
import { epochDay } from "../../lib/date";
import type { Profile, SrsCard } from "../../state/profile";

export type CardStatus = "untouched" | "learning" | "due" | "cooling" | "mastered";

export function cardStatus(cardId: string, profile: Profile, today: string): CardStatus {
  const card = profile.cards[cardId];
  if (!card) return "untouched";
  if (isMastered(card)) return "mastered";
  if (card.interval < GRADUATION_DAYS) return "learning";
  if (card.due <= epochDay(today)) return "due";
  return "cooling";
}

export function daysUntilDue(card: SrsCard | undefined, today: string): number | null {
  if (!card) return null;
  return card.due - epochDay(today);
}

/** left-edge heat color for a Library row: graphite when untouched, a
    cobalt(cold)->phosphor(hot) blend otherwise, phosphor-locked once
    mastered. */
export function heatColor(cardId: string, profile: Profile, today: string): string {
  const card = profile.cards[cardId];
  if (!card) return "var(--fg-faint)";
  if (isMastered(card)) return "var(--accent)";
  const heat = heatFor(card, today);
  return `color-mix(in srgb, var(--accent) ${Math.round(heat * 100)}%, var(--cobalt))`;
}

export const STATUS_LABEL: Record<CardStatus, string> = {
  untouched: "New",
  learning: "Learning",
  due: "Due for a tune-up",
  cooling: "Cooling",
  mastered: "Mastered",
};

export const CATEGORY_LABEL: Record<FluencyCategoryId, string> = {
  smalltalk: "Opening & Small Talk",
  agree: "Agreeing",
  disagree: "Disagreeing (Politely)",
  soften: "Softening an Opinion",
  react: "Reacting & Empathizing",
  clarify: "Clarifying & Buying Time",
  transition: "Transitioning",
  wrapup: "Wrapping Up",
};

export function cardMatchesQuery(card: Card, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return (
    card.text.toLowerCase().includes(q) ||
    (card.meaning?.toLowerCase().includes(q) ?? false) ||
    (card.gloss?.toLowerCase().includes(q) ?? false)
  );
}
